import React, { useState } from 'react';
import StackBar from './StackBar';
import ScoreHistoryChart from './ScoreHistoryChart';
import { getTierForScore } from './scoreTiers';
import { get } from 'lodash';

const ReliabilityBlock = ({
  showJpStats = true,
  showContactInfo,
  at,
  statistics,
  onClickShowContact,
  showEmail,
  showPhone,
}) => {
  const [tipOpen, setTipOpen] = useState(false);

  const trend = get(statistics, 'reliability.scoreTrend', []);
  const score = get(statistics, 'reliability.currentScore', 0) * 10;
  const calloutCount = statistics.totalCallouts;
  const tier = getTierForScore(score);
  const delta = trend.length > 0 ? score - trend[0].v : 0;
  const streakCount = get(statistics, 'reliability.currentStreak', 0);
  const nextMilestone = [5, 10, 25, 50].find(m => m > streakCount) || Math.ceil((streakCount + 1) / 25) * 25;
  const lowData = false;

  const firstMonth = trend.length > 0 ? trend[0].m[0] + trend[0].m.slice(1).toLowerCase() : null;
  const deltaText = firstMonth
    ? (delta > 0 ? '+' : delta < 0 ? '\u2212' : '') + Math.abs(delta) + ` pts since ${firstMonth}`
    : '';
  const deltaColor = tier.text;
  const deltaIcon = delta > 0 ? 'trending_up' : delta < 0 ? 'trending_down' : 'trending_flat';

  const CALLOUT_SEGMENTS = [
    { label: '7\u20133 days notice', value: statistics.callout3To7Days, color: '#C8CDD4' },
    { label: '72\u201324 hours notice', value: statistics.callout1To3Days, color: '#8893A0' },
    { label: 'Under 24 hours', value: statistics.calloutUnder1Day, color: '#374151' },
  ];

  return (
    <div className="reliability-block">
      <div className="at-card__header at-card__header--dark">
        <span className="material-icons-outlined" style={{ fontSize: 16, color: '#5CC4BB' }}>
          bolt
        </span>
        <span>RELIABILITY</span>
      </div>

      <div className="reliability-block__score-row">
        <div className="reliability-block__score-col">
          <div className="reliability-block__score-line">
            <span className="reliability-block__score-number">{score}</span>
            <span className="reliability-block__score-denom">/ 1000</span>
          </div>
          <div className="reliability-block__tier-row">
            <span
              className="reliability-block__tier-badge"
              style={{ color: tier.text, background: tier.bg, borderColor: tier.border }}
            >
              {tier.label.toUpperCase()}
            </span>
            <span
              className="reliability-block__info-wrap"
              onMouseEnter={() => setTipOpen(true)}
              onMouseLeave={() => setTipOpen(false)}
            >
              <span
                className="material-icons-outlined"
                style={{ fontSize: 16, color: '#9CA3AF', cursor: 'help' }}
              >
                info
              </span>
              {tipOpen && (
                <span className="reliability-block__tooltip">
                  Score is capped at 1000 and floored at 0, new ATs start at 730. All penalties automatically
                  fall off exactly 6 months from the incident date. ATs build streaks and pick up coverage
                  shifts to recover score and are progressively penalized for last minute callouts and no
                  shows.
                  <span className="reliability-block__tooltip-tiers">
                    <span>EXCELLENT - 900 - 1000</span>
                    <span>VERY GOOD - 800 - 899</span>
                    <span>GOOD - 670 - 799</span>
                    <span>FAIR - 500 - 669</span>
                    <span>POOR - 0 - 499</span>
                  </span>
                </span>
              )}
            </span>
          </div>
          {deltaText && (
            <div className="reliability-block__delta" style={{ color: deltaColor }}>
              <span className="material-icons-outlined" style={{ fontSize: 15 }}>
                {deltaIcon}
              </span>
              {deltaText}
            </div>
          )}
        </div>
        <div className="reliability-block__trend-col">
          <ScoreHistoryChart data={trend} />
        </div>
      </div>

      {lowData && (
        <div className="reliability-block__low-data">
          <span className="material-icons-outlined" style={{ fontSize: 16, color: '#854D0E' }}>
            schedule
          </span>
          <div>
            <strong>Limited history.</strong> New ATs start at 730, so this score reflects the baseline more
            than behavior.
          </div>
        </div>
      )}

      <div className="reliability-block__lifetime-heading">
        <span>LIFETIME TOTALS</span>
      </div>

      <div className="reliability-block__lifetime-grid">
        <div className="reliability-block__lifetime-cell reliability-block__lifetime-cell--bordered">
          <div className="reliability-block__lifetime-number">{statistics.jobCount}</div>
          <div className="reliability-block__lifetime-label">Shifts worked</div>
          {showJpStats && statistics.withYou.jobs > 0 && (
            <div className="reliability-block__lifetime-jp">{statistics.withYou.jobs} with you</div>
          )}
        </div>
        <div className="reliability-block__lifetime-cell reliability-block__lifetime-cell--bordered">
          <div className="reliability-block__lifetime-number">{calloutCount}</div>
          <div className="reliability-block__lifetime-label">Callouts</div>

          {showJpStats && statistics.withYou.callouts > 0 && (
            <div className="reliability-block__lifetime-jp">{statistics.withYou.callouts} with you</div>
          )}
          {calloutCount > 0 && statistics.withYou.callouts > 0 && (
            <div className="reliability-block__callout-bar">
              <StackBar
                tooltipTitle={`${calloutCount} ${calloutCount === 1 ? 'callout' : 'callouts'}, lifetime`}
                segments={CALLOUT_SEGMENTS}
                height={8}
                showLegend={false}
              />
              <div className="reliability-block__callout-note">By notice given</div>
            </div>
          )}
        </div>
        <div className="reliability-block__lifetime-cell">
          <div className="reliability-block__lifetime-number">{statistics.noShowCount}</div>
          <div className="reliability-block__lifetime-label">No-shows</div>
          {showJpStats && statistics.withYou.noShows > 0 && (
            <div className="reliability-block__lifetime-jp">{statistics.withYou.noShows} with you</div>
          )}
        </div>
      </div>

      <div className="reliability-block__streak">
        <span className="material-icons-outlined" style={{ fontSize: 17, color: '#3A9C95' }}>
          local_fire_department
        </span>
        <span className="reliability-block__streak-count">{streakCount} shift streak</span>
        <span style={{ flex: 1 }} />
        <span className="reliability-block__streak-note">next milestone at {nextMilestone}</span>
      </div>

      {showJpStats && (statistics.messageForEo === 'YES' || statistics.messageForEo === 'NO') && (
        <div className="reliability-block__hire-again">
          <span className="reliability-block__hire-again-label">Would I hire again?</span>
          {statistics.messageForEo === 'YES' && (
            <span className="reliability-block__hire-badge reliability-block__hire-badge--yes">
              <span className="material-icons-outlined" style={{ fontSize: 15, color: '#059669' }}>
                thumb_up
              </span>
              <span>YES</span>
            </span>
          )}
          {statistics.messageForEo === 'NO' && (
            <span className="reliability-block__hire-badge reliability-block__hire-badge--no">
              <span className="material-icons-outlined" style={{ fontSize: 15, color: '#991B1B' }}>
                thumb_down
              </span>
              <span>NO</span>
            </span>
          )}
          <span className="reliability-block__hire-again-note">
            Your rating after {statistics.withYou.jobs} shifts
          </span>
        </div>
      )}

      {showContactInfo && (
        <div className="reliability-block__contact">
          <div className="reliability-block__contact-heading">Contact information</div>
          <p>
            <b>Email:</b>{' '}
            {showEmail ? (
              <span>{at.email}</span>
            ) : (
              <span className="reliability-block__contact-link" onClick={() => onClickShowContact('email')}>
                Click to view
              </span>
            )}
          </p>
          <p>
            <b>Phone:</b>{' '}
            {showPhone ? (
              <span>{at.phoneNumber}</span>
            ) : (
              <span className="reliability-block__contact-link" onClick={() => onClickShowContact('phone')}>
                Click to view
              </span>
            )}
          </p>
        </div>
      )}
    </div>
  );
};

export default ReliabilityBlock;
